import { z } from 'zod';
import { ChartOptionsSchema } from './chart-options.js';
import { AnalysisPlanSchema, AnalysisExecutionSchema } from './analysis-plan.js';
import { ProductAnalysisSpecSchema, ProductAnalysisResultSchema } from './product-analysis.js';
import { QuerySpecSchema, QueryResultSchema, } from './metrics-catalog.js';
export const PanelViewTypeSchema = z.enum([
    'stat',
    'timeseries',
    'bar',
    'table',
    'ranking',
    'donut',
    'summary',
]);
export const PANEL_VIEW_LABELS = {
    stat: '숫자 타일',
    timeseries: '시계열',
    bar: '범주 막대',
    table: '데이터 테이블',
    ranking: '순위 리스트',
    donut: '비중',
    summary: '텍스트 요약',
};
export const PanelOptionsSchema = z.object({
    chart: ChartOptionsSchema.optional(),
    sort: z.enum(['asc', 'desc']).default('desc'),
    sortKey: z.string().max(64).optional(),
    topN: z.number().int().min(1).max(50).default(10),
    showReleaseMarkers: z.boolean().default(false),
});
export const PanelInputSchema = z.object({
    title: z.string().trim().min(1).max(100),
    query: QuerySpecSchema,
    viewType: PanelViewTypeSchema,
    viewOptions: PanelOptionsSchema.default({
        sort: 'desc',
        topN: 10,
        showReleaseMarkers: false,
    }),
    position: z
        .object({
        order: z.number().int().min(0).max(10000),
        span: z.union([z.literal(1), z.literal(2), z.literal(4)]),
    })
        .default({ order: 0, span: 2 }),
});
export const PanelPresentationInputSchema = PanelInputSchema.pick({ title: true, viewType: true, viewOptions: true });
export const AnalyticsPanelSchema = PanelInputSchema.extend({
    id: z.string(),
    boardId: z.string(),
});
export const BoardInputSchema = z.object({
    name: z.string().trim().min(1).max(100),
    isShared: z.boolean().default(false),
});
export const AnalyticsBoardSchema = BoardInputSchema.extend({
    id: z.string(),
    ownerId: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const BoardDetailSchema = AnalyticsBoardSchema.extend({
    panels: z.array(AnalyticsPanelSchema),
});
export const AnalyticsModelTierSchema = z.enum(['haiku', 'sonnet46', 'sonnet', 'opus']);
export const AskInputSchema = z.object({
    question: z.string().trim().min(1).max(2000),
    previousQuery: QuerySpecSchema.optional(),
    modelTier: AnalyticsModelTierSchema.optional(),
});
export const QuestionInterpretationSchema = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('resolved'),
        title: z.string().min(1).max(100),
        query: QuerySpecSchema,
    }),
    z.object({
        status: z.literal('missing'),
        reason: z.string().min(1).max(1000),
    }),
]);
export const AskResponseSchema = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('resolved'),
        title: z.string(),
        query: QuerySpecSchema,
        result: QueryResultSchema,
    }),
    z.object({ status: z.literal('missing'), reason: z.string() }),
]);
export const DashboardChatInputSchema = z.object({
    boardRange: QuerySpecSchema.shape.range.optional(),
    message: z.string().trim().min(1).max(2000),
    history: z.array(z.object({ role: z.enum(['user', 'assistant']), text: z.string().max(3000) })).max(12).default([]),
    modelTier: AnalyticsModelTierSchema.default('sonnet46'),
    panelId: z.string().min(1).max(128).optional(),
    previousQuery: QuerySpecSchema.optional(),
});
export const DashboardChatInterpretationSchema = z.discriminatedUnion('status', [
    ...QuestionInterpretationSchema.options,
    z.object({ status: z.literal('answer'), text: z.string().min(1).max(3000) }),
]);
export const DashboardChatResponseSchema = z.discriminatedUnion('status', [
    ...AskResponseSchema.options,
    z.object({ status: z.literal('answer'), text: z.string().min(1).max(3000) }),
]);
export const RecentQuestionSchema = z.object({
    id: z.string(),
    question: z.string(),
    resolvedQuery: QuerySpecSchema.nullable(),
    missingDefinition: z.string().nullable(),
    createdAt: z.string(),
});
export const AnalyticsMutationResponseSchema = z.unknown();
export const AnalyticsReleaseMarkersSchema = z.array(z.object({ date: z.string(), tag: z.string(), label: z.string() }));
/** 표시 형태의 적합성은 서버와 화면에서 같은 규칙으로 검사한다. */
export function panelViewReason(view, query, formats) {
    if (view === 'table' || view === 'summary')
        return null;
    if (view === 'timeseries') {
        if (!query.grain)
            return '시계열에는 집계 단위가 필요합니다';
        if (new Set(formats).size > 2)
            return '단위가 3종 이상이면 데이터 테이블을 선택하세요';
        return null;
    }
    if (query.grain)
        return '집계 단위가 있는 결과는 시계열이나 데이터 테이블로 표시하세요';
    if (query.metrics.length !== 1)
        return '지표 하나를 선택하세요';
    if (view === 'stat')
        return query.dimensions.length === 0
            ? null
            : '숫자 타일에는 축을 사용하지 않습니다';
    return query.dimensions.length === 1 ? null : '축 하나를 선택하세요';
}
export function choosePanelView(query, result) {
    const formats = result.columns
        .filter((c) => c.kind === 'metric')
        .map((c) => c.format ?? 'number');
    if (query.metrics.length >= 3 || query.dimensions.length >= 2)
        return 'table';
    if (query.grain)
        return panelViewReason('timeseries', query, formats)
            ? 'table'
            : 'timeseries';
    if (!query.dimensions.length && query.metrics.length === 1)
        return 'stat';
    if (query.dimensions.length === 1 && query.metrics.length === 1)
        return result.rows.length <= 8 ? 'bar' : 'ranking';
    return 'table';
}
/** 보드·패널 저장과 독립적으로 저장된 지표 정의를 조회하는 분석 도우미 계약. */
export const ProductAnalysisChatInputSchema = DashboardChatInputSchema.pick({
    message: true, history: true, modelTier: true,
}).extend({
    previousAnalysis: ProductAnalysisSpecSchema.optional(),
    previousPlan: AnalysisPlanSchema.optional(),
    draftPlan: AnalysisPlanSchema.optional(),
    action: z.enum(['plan', 'review', 'execute']).default('plan'),
    modelTier: AnalyticsModelTierSchema.default('sonnet'),
});
export const ProductAnalysisChatResponseSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('cancelled'), text: z.string().min(1).max(3000) }),
    z.object({ status: z.literal('plan'), plan: AnalysisPlanSchema, conditions: z.array(z.string()), ready: z.boolean() }),
    z.object({ status: z.literal('result'), plan: AnalysisPlanSchema, text: z.string().min(1).max(6000), results: z.array(AnalysisExecutionSchema).max(6) }),
    z.object({ status: z.literal('analysis'), analysis: ProductAnalysisResultSchema }),
    z.object({ status: z.literal('answer'), text: z.string().min(1).max(3000) }),
    z.object({ status: z.literal('missing'), reason: z.string().min(1).max(3000) }),
]);
//# sourceMappingURL=analytics-board.js.map